/**
 * FauxDS TextArea - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate TextArea component for prototyping.
 * It is NOT the real @faire/slate TextArea and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate TextArea, see: @faire/slate/components/text-area
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 6189:17242 (Slate Text Area)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Default, Active, Filled, Error, Disabled
 * - Character limit support
 * - Helper text support
 * - Resize control (web only)
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, {
  forwardRef,
  useId,
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  useEffect,
} from "react";

export type TextAreaState = "Default" | "Active" | "Filled";

export interface TextAreaProps {
  /**
   * Label text displayed above the text area
   * @default "Label"
   */
  label?: string;
  /**
   * When `true`, shows error styling; when a string, shows that message below the field
   */
  error?: boolean | string;
  /**
   * When defined, shows the character counter and sets `maxLength` on the textarea
   */
  characterLimit?: number;
  /**
   * Current value (controlled)
   */
  value?: string;
  /**
   * **playground only**
   * Default value text (uncontrolled)
   */
  defaultValueText?: string;
  /**
   * Placeholder text; when defined, the placeholder is shown
   */
  placeholder?: string;
  /**
   * **playground only**
   * Whether the text area is focused
   * @default false
   */
  focused?: boolean;
  /**
   * Helper text displayed below the text area; when defined, the helper row can show
   */
  helper?: string;
  /**
   * **playground only**
   * Override text for the character counter (e.g. "0/500")
   */
  characterLimitText?: string;
  /**
   * **playground only**
   * Whether to show footer section
   * @default false
   */
  footer?: boolean;
  /**
   * Whether the textarea is vertically resizable
   * @default true
   */
  resize?: boolean;
  /**
   * **playground only**
   * Accessibility label for screen readers
   * @default "Label"
   */
  "aria-label"?: string;
  /**
   * Whether the text area is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * **playground only**
   * State of the text area
   * @default "Default"
   */
  state?: TextAreaState;
  /**
   * Change handler
   */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * **playground only**
   * Text area name attribute
   */
  name?: string;
  /**
   * **playground only**
   * Number of visible rows
   * @default 3
   */
  rows?: number;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * TextArea - FauxDS (recreated from Figma MCP)
 *
 * A Text Area provides users with a free-form space to enter and edit long strings of text.
 * Text areas are ideal for tasks that require a lot of text input and may have optional character limits.
 *
 * Usage tips:
 * - Use when the user needs to input more than one line of text.
 * - Be intentional about setting a character limit. Ensure that it is large enough for the task at hand.
 * - Avoid stretching the Input across the full width of wider screens, define a maximum width based on your use case.
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label = "Label",
      error,
      characterLimit,
      value,
      defaultValueText,
      placeholder,
      focused = false,
      helper,
      characterLimitText,
      footer = false,
      resize = true,
      "aria-label": ariaLabel,
      disabled = false,
      state = "Default",
      onChange,
      name,
      rows = 3,
      className = "",
    },
    ref
  ) => {
    const generatedId = useId();
    const textareaId = `textarea-figma-${generatedId}`;
    const helperId = `helper-figma-${generatedId}`;

    const [internalValue, setInternalValue] = useState(
      value ?? defaultValueText ?? ""
    );

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    const currentCount = currentValue.length;

    const displayCharacterLimitText =
      characterLimitText ??
      (characterLimit !== undefined
        ? `${currentCount}/${characterLimit}`
        : undefined);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        if (!isControlled) {
          setInternalValue(newValue);
        }
        onChange?.(e);
      },
      [onChange, isControlled]
    );

    const hasError = !!error;
    const errorMessage = typeof error === "string" ? error : undefined;
    const showHelper = !!helper && !hasError;
    const showCharacterLimit = characterLimit !== undefined;
    const showFooter = footer || showHelper || hasError || showCharacterLimit;

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => textareaRef.current!);
    useEffect(() => {
      if (focused && textareaRef.current) {
        textareaRef.current.focus();
      }
    }, [focused]);

    const getStateStyles = () => {
      if (disabled) {
        return {
          label: "text-[var(--slate-color-action-text-disabled)]", // #757575
          border: "border-[var(--slate-color-action-border-disabled)]", // #dfe0e1
          bg: "bg-[var(--slate-color-action-surface-disabled)]", // #f7f7f7
          text: "text-[var(--slate-color-action-text-disabled)]", // #757575
          helper: "text-[var(--slate-color-action-text-disabled)]", // #757575
          characterLimit: "text-[var(--slate-color-action-text-disabled)]", // #757575
        };
      }
      if (hasError) {
        return {
          label: "text-[var(--slate-color-message-text-critical)]", // #d32f2f
          border: "border-[var(--slate-color-action-border-critical)]", // #d32f2f
          bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
          text: "text-[var(--slate-color-action-text-default)]", // #333333
          helper: "text-[var(--slate-color-message-text-critical)]", // #d32f2f
          characterLimit: "text-[var(--slate-color-action-text-subdued)]", // #757575
        };
      }
      if (state === "Active" || focused) {
        return {
          label: "text-[var(--slate-color-action-text-default)]", // #333333
          border: "border-[var(--slate-color-action-border-active)]", // #000000
          bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
          text: "text-[var(--slate-color-action-text-active)]", // #000000
          helper: "text-[var(--slate-color-action-text-subdued)]", // #757575
          characterLimit: "text-[var(--slate-color-action-text-subdued)]", // #757575
        };
      }
      return {
        label: "text-[var(--slate-color-action-text-default)]", // #333333
        border: "border-[var(--slate-color-action-border-subdued)]", // #dfe0e1
        bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
        text: "text-[var(--slate-color-action-text-default)]", // #333333
        helper: "text-[var(--slate-color-action-text-subdued)]", // #757575
        characterLimit: "text-[var(--slate-color-action-text-subdued)]", // #757575
      };
    };

    const stateStyles = getStateStyles();

    return (
      <div className={cn("flex flex-col", className)}>
        {label && (
          <label
            htmlFor={textareaId}
            className={cn("mb-[8px]", stateStyles.label)}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "14px",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-100)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {label}
          </label>
        )}

        <textarea
          ref={textareaRef}
          id={textareaId}
          name={name}
          value={isControlled ? value : undefined}
          defaultValue={!isControlled ? defaultValueText : undefined}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={characterLimit}
          rows={Math.max(2, rows)}
          aria-label={ariaLabel}
          aria-invalid={hasError}
          aria-describedby={showFooter ? helperId : undefined}
          className={cn(
            "w-full px-[16px] py-[12px]",
            stateStyles.border,
            stateStyles.bg,
            stateStyles.text,
            "rounded-[var(--slate-radius-component-default)] border transition-all duration-150 ease-in-out placeholder:text-[var(--slate-color-action-text-placeholder)]",
            resize ? "resize-y" : "resize-none",
            disabled ? "cursor-not-allowed" : "cursor-text",
            "focus:outline-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--slate-color-action-border-focus-ring)]"
          )}
          style={{
            fontFamily: "var(--slate-typography-family-sans-serif)",
            fontSize: "var(--slate-typography-font-size-200)",
            fontWeight: "var(--slate-typography-weight-regular)",
            lineHeight: "var(--slate-typography-line-height-200)",
            letterSpacing: "var(--slate-typography-letter-spacing-200)",
          }}
        />

        {showFooter ? (
          <div
            id={helperId}
            className={cn("mt-[4px] flex items-center justify-between")}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "var(--slate-typography-font-size-100)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-100)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            <span className={stateStyles.helper}>
              {hasError ? errorMessage : helper ? helper : ""}
            </span>
            {showCharacterLimit && displayCharacterLimitText ? (
              <span className={cn("ml-2 shrink-0", stateStyles.characterLimit)}>
                {displayCharacterLimitText}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
);

TextArea.displayName = "TextArea";

export default TextArea;
